import React, { useEffect, useRef, useState, useImperativeHandle } from 'react';
import './index.module.less';
import { debounce } from 'lodash';
import BaseMap from '@components/map/Map/BaseMap';
import { defaultPoint } from '@helpers/Constants';
import List from './List';
import SearchList from './SearchList';
import { showLoading, hideLoading } from '@widgets/Loading';
import Toast from '@widgets/Toast';
import { Api } from '@apis/client';
import Http from '@helpers/Http';
import { ICenterPoint, IPlaceItem } from '../constants';

//私有常量
const maxZoom = 18;
const minZoom = 1;

//可抽离的逻辑处理函数/组件

let MapDetail = React.forwardRef(
  (props: IProps, ref): JSX.Element => {
    //变量声明、解构
    const mapRef = useRef(null);
    const timer = useRef(null);
    const { centerPoint, urlParams } = props;

    //组件状态
    const [mapCenter, setMapCenter] = useState(centerPoint);
    const [locations, setLocations] = useState(null);
    const [searchLocations, setSearchLocations] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [searchListVisible, setSearchListVisible] = useState(false);

    //网络IO

    //数据转换
    const mapContainerHeight = document.body.clientHeight * 0.77;

    //逻辑处理函数
    // 初始化地图
    const initMapEvent = () => {
      const instanceMap = mapRef.current;
      const tempLocation = mapCenter;
      instanceMap?.setCenter(tempLocation);
      setMapCenter(tempLocation);
      getPointLocation(tempLocation);
      instanceMap.addListener('dragend', getTargeDetail);
      hideLoading();
    };

    const onMapLoad = () => {
      console.log('map load');
    };

    // 获取当前位置（设备所在的位置）
    const getLocationPosition = async () => {
      setSelectedLocation(null);
      const { data } = await Http.post(Api.GetCardInfoBySncode, {
        snCode: urlParams.snCode,
        company: urlParams.company || 'lingge',
      });

      // 如果设备离线
      if (!data?.isOnLine) {
        Toast.show({
          type: 'strip',
          title: '设备离线，请联网后设置',
        });
      }

      //设备在线时下发实时定位指令
      if (data?.isOnLine) {
        await Http.post(Api.PostPositionV1Realtime, {
          snCode: urlParams.snCode,
        }).catch(err => {});
      }

      // 获取定位信息(如果设备有历史定位，则使用历史定位，没有历史定位时使用默认位置为北京市定位
      const { data: result } = await Http.post(Api.GetPositionV1Realtime, {
        snCode: urlParams.snCode,
      });
      let location = defaultPoint;
      if (result) {
        location = { lng: result.longitude, lat: result.latitude };
      }
      const instanceMap = mapRef.current;
      instanceMap.setCenter(location);
      getPointLocation(location);
      setMapCenter(location);
    };

    // 根据当前地址查找附近位置
    const getPointLocation = async (centers?: ICenterPoint) => {
      const centerData = centers;
      const instanceMap = mapRef.current;
      const list = await instanceMap.getLocationPois(centerData, 100);
      setLocations(list);
      setSelectedLocation(list[0]);
    };

    //获取移动后的中心点坐标（拖拽触发）
    const getTargeDetail = () => {
      const instanceMap = mapRef.current;
      setSelectedLocation(null);
      const p = instanceMap.getCenter();
      const targetPoint = p;
      setMapCenter(targetPoint);
      getPointLocation(targetPoint);
    };

    // 开启搜索状态
    const handleOpenSearch = () => {
      setSearchListVisible(true);
    };

    // 取消搜索搜索状态
    const handleCancelSearch = () => {
      setSearchListVisible(false);
      setSearchLocations([]);
    };

    //根据输入地点模糊查找位置信息（防抖）
    const search = debounce(async (value?: string) => {
      if (!value) {
        setSearchLocations([]);
        hideLoading();
        return;
      }
      clearTimeout(timer.current);
      if (!navigator.onLine) {
        return Toast.show({
          type: 'strip',
          title: '网络异常，请重试',
        });
      }
      showLoading();
      //搜索超时限制6s
      timer.current = setTimeout(() => {
        hideLoading();
        Toast.show({
          type: 'strip',
          title: '网络异常，请重试',
        });
      }, 6000);
      const instanceMap = mapRef.current;
      const result = await instanceMap.locationSearch(value, mapCenter);
      hideLoading();
      clearTimeout(timer.current);
      setSearchLocations(result || []);
    }, 500);

    // 选择List中的一个位置
    const handleSelectItem = (item: IPlaceItem, isSearch?: boolean) => {
      setSearchListVisible(false);
      setSelectedLocation(item);
      const instanceMap = mapRef.current;

      setSearchLocations(null);
      const c = {
        lng: item.lng || item.point.lng,
        lat: item.lat || item.point.lat,
      };
      instanceMap.setCenter(c);
      getPointLocation(c);
      setMapCenter(c);
    };

    // 保存数据
    const saveData = () => {
      return {
        title: selectedLocation.title,
        address: selectedLocation.address || selectedLocation.title,
        points: [[selectedLocation.lng, selectedLocation.lat]],
      };
    };

    useImperativeHandle(ref, () => ({
      save: saveData,
      reset: () => {},
      getMapCenter: () => mapCenter,
    }));

    //组件Effect
    useEffect(() => {
      showLoading();
      setTimeout(() => {
        initMapEvent();
      }, 1500);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      return () => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const instanceMap = mapRef.current;
        if (instanceMap) {
          instanceMap?.removeListener('dragend', getTargeDetail);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      // 记录窗口高度，避免 安卓手机 键盘挤小屏幕导致页面变形
      document.body.style.overflow = 'hidden';
      const originalHeight =
        document.documentElement.clientHeight || document.body.clientHeight;
      window.onresize = function() {
        document.body.style.height = originalHeight + 'px';
      };

      return () => {
        window.onresize = null;
        document.body.style.overflow = 'auto';
      };
    }, []);

    return (
      <div style={{ width: '100%', height: mapContainerHeight }}>
        <BaseMap
          ref={mapRef}
          onMapLoad={onMapLoad}
          center={mapCenter}
          maxZoom={maxZoom}
          minZoom={minZoom}
          zoom={18}
          enablePinchToZoom={true}
          enableDoubleClickZoom={false}
        >
          {' '}
        </BaseMap>
        <div
          styleName="map-point"
          style={{
            top: (mapContainerHeight - document.body.clientWidth * 0.14) / 2,
          }}
        />
        <List
          items={locations}
          selectedLocation={selectedLocation}
          onShowSearch={handleOpenSearch}
          onSelectItem={handleSelectItem}
          onLocationPosition={getLocationPosition}
        />
        {searchListVisible && (
          <SearchList
            items={searchLocations}
            onSelectItem={handleSelectItem}
            selectedLocation={selectedLocation}
            onCancel={handleCancelSearch}
            onSearch={search}
          />
        )}
      </div>
    );
  },
);

//props类型定义
interface IProps {
  /**
   * 地图中心的经纬度（用于定位）
   */
  centerPoint?: ICenterPoint;
  /**
   * 路由参数（判断是添加还是编辑）
   */
  urlParams: { [key: string]: string };
}

//prop-type定义，可选

export default MapDetail;
